// Buddies (docs/COLLABORATION_DESIGN.md): inviting people to work on a
// program together, live, and joining from an invite link. The page side of
// a session is session.js; this is its panel and its place in the IDE.
//
// The host presses Invite a buddy: the project is saved to their account if
// it is not there yet, the server opens a room for it, and the panel shows
// an edit link and a watch link. A link opens the IDE with the session's
// project, kept in the browser for the session; Save a copy keeps one. The
// host can make new links, turn watchers who are not signed in away, remove
// people, run the program for everyone, and end the session.

import { BuddySession, inviteLink, parseInvite } from "./session.js";
import { hashBlob } from "../cloud.js";

const element = (tag, className, text) => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
};
const button = (text, title, action, className = "") => {
  const node = element("button", className, text);
  node.type = "button";
  if (title) node.title = title;
  node.addEventListener("click", action);
  return node;
};
const ROLE = { host: "Host", editor: "Can edit", watcher: "Watching" };

// ide: { $, cloud, store, editor, account, notice, clearNotices, status, ask,
//   project(), show(project), openFile(path), closeFile(path),
//   refreshFiles(), metaChanged(), saveAll(), isText(path), tree,
//   runFiles({ files, main, mode, by }), sessionChanged() (a session began
//   or ended here) }
export function setupBuddies(ide) {
  const { $, cloud, store, editor, notice, clearNotices, status } = ide;
  if (!cloud || !cloud.config.buddies) return null;
  const signedIn = () => !!cloud.user;
  let session = null;
  let unread = 0;
  let following = null;             // the person followed, by id
  let runsAllowed = false;          // Run for everyone, allowed for this session
  let cursorFrame = 0;

  // The menu bar's button and badge.
  const inviteButton = $("buddies-button");
  const chip = $("buddies-chip");
  inviteButton.hidden = false;

  // The panel, under the menu bar at the right.
  const panel = element("section", "buddies-panel");
  panel.hidden = true;
  panel.setAttribute("role", "dialog");
  panel.setAttribute("aria-label", "Buddies");
  const head = element("header", "buddies-head");
  const title = element("h2", "", "Buddies");
  const stateText = element("span", "buddies-state muted");
  head.append(title, stateText, button("×", "Close", () => toggle(false), "buddies-close"));
  const body = element("div", "buddies-body");
  panel.append(head, body);
  document.body.append(panel);
  // Esc closes the panel, and gives the editor the keyboard back.
  panel.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    event.preventDefault();
    toggle(false);
    editor.focus();
  });

  // The host's invite links and controls.
  const invite = element("section", "buddies-invite");
  const linkRow = (label, help) => {
    const row = element("div", "buddies-link");
    const caption = element("label", "", label);
    const field = element("input");
    field.type = "text";
    field.readOnly = true;
    field.setAttribute("aria-label", label);
    field.addEventListener("focus", () => field.select());
    const copy = button("Copy", help, async () => {
      try {
        await navigator.clipboard.writeText(field.value);
        status(`Copied the ${label.toLowerCase()}.`);
      } catch {
        field.focus();
        status("Copy the selected link with Ctrl+C.");
      }
    });
    const line = element("div", "buddies-row");
    line.append(field, copy);
    row.append(caption, line);
    return { row, field };
  };
  const editLink = linkRow("Edit link", "Copy a link for people who will change the program with you");
  const watchLink = linkRow("Watch link", "Copy a link for people who will follow along");
  const anonymousLabel = element("label", "check");
  const anonymous = element("input");
  anonymous.type = "checkbox";
  anonymousLabel.append(anonymous, " Allow watchers who aren't signed in");
  anonymous.addEventListener("change", () => attempt("The setting could not be changed", async () => {
    await cloud.request("PATCH", `rooms/${session.roomId}`, { json: { allowAnonymous: anonymous.checked } });
  })());
  const hostButtons = element("div", "buddies-row buddies-actions");
  hostButtons.append(
    button("Make new links", "Make new invite links. The old ones stop working; people already here stay.",
      () => attempt("New links could not be made", async () => {
        const { room } = await cloud.request("POST", `rooms/${session.roomId}/links`, { json: {} });
        session.room.links = { edit: room.editToken, watch: room.watchToken };
        render();
        status("Made new invite links. The old ones no longer work.");
      })()),
    button("Run for everyone", "Run the program on everyone's screen, as it is now", () => runForEveryone()),
    button("End session", "End the buddy session for everyone. Your project keeps every change.",
      () => endSession(), "danger"));
  invite.append(element("p", "muted", "Anyone signed in with the edit link can change this program with you. " +
    "Anyone with the watch link can follow along."), editLink.row, watchLink.row, anonymousLabel, hostButtons);

  // Signed out, or not yet started.
  const intro = element("section", "buddies-intro");

  // The people here.
  const peopleSection = element("section", "buddies-people");
  const peopleList = element("ul");
  peopleSection.append(element("h3", "", "People"), peopleList);

  // Messages.
  const messagesSection = element("section", "buddies-messages");
  const messageList = element("ol", "buddies-log");
  messageList.setAttribute("role", "log");
  messageList.setAttribute("aria-live", "polite");
  messageList.setAttribute("aria-label", "Messages in the session");
  const form = element("form", "buddies-row");
  const input = element("input");
  input.type = "text";
  input.maxLength = 2000;
  input.placeholder = "Message everyone";
  input.setAttribute("aria-label", "Message everyone");
  const send = element("button", "", "Send");
  send.type = "submit";
  send.title = "Send the message to the people in the session";
  // Blip, when the server offers him: one request at a time for the room,
  // paid from the allowance of the person who asks.
  const askBlip = button("Ask Blip", "Ask Blip, for everyone to see", () => {
    const text = input.value.trim();
    if (!text || !ide.blip()) return;
    input.value = "";
    // His own panel shows the conversation; everyone's log has it too.
    toggle(false);
    ide.blip().ask(text);
  });
  form.append(input, send, askBlip);
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    const text = input.value.trim();
    if (!text || !session) return;
    session.say(text);
    input.value = "";
  });
  const blipLine = element("p", "buddies-blip muted");
  const catchUpButton = button("Catch me up", "Ask Blip what the others changed since you joined, or since you last asked",
    () => catchUp());
  const blipRow = element("div", "buddies-row buddies-actions");
  blipRow.append(catchUpButton);
  const picturesNote = element("p", "buddies-note muted",
    "Questions for Blip, and the pictures shown with them, are seen by everyone in the session.");
  messagesSection.append(element("h3", "", "Messages"), messageList, blipLine, form, blipRow, picturesNote);
  // A question's picture, large: a click or Esc closes it.
  const viewerPicture = element("img");
  const viewer = element("div", "buddies-viewer");
  viewer.hidden = true;
  viewer.setAttribute("role", "dialog");
  viewer.setAttribute("aria-label", "A picture, large");
  viewer.tabIndex = -1;
  viewer.append(viewerPicture);
  const closeViewer = () => {
    viewer.hidden = true;
    viewerPicture.removeAttribute("src");
  };
  viewer.addEventListener("click", closeViewer);
  viewer.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    event.stopPropagation();
    closeViewer();
  });
  panel.append(viewer);
  // The questions' pictures, fetched from the room once (hash -> a promise
  // of an object URL), and those this person hid on their own screen.
  let pictureUrls = new Map();
  const hiddenPictures = new Set();

  // A guest's choices.
  const guest = element("section", "buddies-row buddies-actions buddies-guest");
  guest.append(
    button("Save a copy", "Keep a copy of the program in this browser, yours to change", () => saveCopy()),
    button("Leave", "Leave the buddy session", () => leave()));
  body.append(intro, invite, peopleSection, messagesSection, guest);

  const attempt = (what, action) => async (...rest) => {
    try {
      return await action(...rest);
    } catch (error) {
      notice(`${what}: ${error.message}`, [], "buddies");
      return undefined;
    }
  };

  // The panel sits under the menu bar's button, and under the notices, so
  // that their buttons stay in reach.
  const place = () => {
    if (panel.hidden) return;
    const top = Math.max(inviteButton.getBoundingClientRect().bottom, $("notices").getBoundingClientRect().bottom);
    panel.style.top = `${Math.round(top + 4)}px`;
  };
  new MutationObserver(place).observe($("notices"), { childList: true, subtree: true });
  addEventListener("resize", place);

  function toggle(open = panel.hidden) {
    panel.hidden = !open;
    inviteButton.setAttribute("aria-expanded", String(open));
    if (open) {
      place();
      unread = 0;
      render();
      if (session && session.state === "open") input.focus();
    }
  }

  // Draws the panel and the menu bar's button as the session is now.
  function render() {
    const open = !!session && !session.stopped;
    const count = open ? session.people.length : 0;
    inviteButton.textContent = open ? `Buddies (${count})${unread ? " •" : ""}` : "Invite a buddy";
    inviteButton.title = open ? "The people working on this program with you" :
      "Invite people to work on this program with you, live";
    chip.hidden = !open;
    chip.textContent = open && session.state === "reconnecting" ? "Reconnecting…" :
      open && !session.isHost && session.room ? `With ${session.room.host}` : "Buddies";
    chip.title = open ? (session.isHost ? "Your buddy session is open" : `In ${session.room?.host ?? "a"}'s buddy session`) : "";
    if (panel.hidden) return;
    intro.replaceChildren();
    intro.hidden = open;
    if (!open) {
      if (!signedIn()) {
        intro.append(element("p", "", "Invite people to work on this program with you: they see your changes as you " +
          "make them, and you see theirs. Inviting needs an account."),
        button("Sign in", "Sign in or make an account", () => $("account-button").click(), "primary"));
      } else {
        intro.append(element("p", "", "Invite people to work on this program with you: they see your changes as you " +
          "make them, and you see theirs."),
        button("Invite a buddy", "Open a buddy session for this program", () => start(), "primary"));
      }
    }
    invite.hidden = !open || !session.isHost;
    if (open && session.isHost && session.room && session.room.links) {
      editLink.field.value = inviteLink(session.roomId, session.room.links.edit);
      watchLink.field.value = inviteLink(session.roomId, session.room.links.watch);
      anonymous.checked = !!session.room.allowAnonymous;
    }
    stateText.textContent = !open ? "" : session.state === "open" ? "" :
      session.state === "reconnecting" ? "Reconnecting…" : "Connecting…";
    peopleSection.hidden = !open;
    messagesSection.hidden = !open;
    guest.hidden = !open || session.isHost;
    if (!open) return;
    // People, the host first.
    peopleList.replaceChildren();
    const people = [...session.people].sort((a, b) => (a.role === "host" ? -1 : 0) - (b.role === "host" ? -1 : 0));
    for (const person of people) {
      const row = element("li");
      const dot = element("span", "buddies-dot");
      dot.style.setProperty("--buddy", person.colour);
      const me = session.you && person.id === session.you.id;
      const name = element("span", "buddies-person", me ? `${person.name} (you)` : person.name);
      const role = element("span", "muted", ROLE[person.role] || "");
      const presence = session.presenceOf(person);
      if (presence && presence.file && !me) name.title = `In ${presence.file}`;
      row.append(dot, name, role);
      if (!me) {
        const follow = button(following === person.id ? "Following" : "Follow",
          following === person.id ? "Stop following" : `Go where ${person.name} is, and stay with them until you click or type`,
          () => setFollowing(following === person.id ? null : person.id), following === person.id ? "pressed" : "");
        follow.setAttribute("aria-pressed", String(following === person.id));
        row.append(follow);
        if (session.isHost && person.role !== "host") {
          row.append(button("Remove", `Remove ${person.name} from the session`, () => removePerson(person), "danger"));
        }
      }
      peopleList.append(row);
    }
    // Messages.
    messageList.replaceChildren();
    for (const message of session.messages.slice(-100)) messageList.append(messageRow(message));
    messageList.scrollTop = messageList.scrollHeight;
    form.hidden = !session.you || !session.you.signedIn;
    // Blip, for people who can edit.
    const blipAvailable = !!ide.blip() && session.canEdit && session.you.signedIn;
    askBlip.hidden = !blipAvailable;
    blipRow.hidden = !blipAvailable;
    picturesNote.hidden = !blipAvailable;
    const blip = session.blipState;
    blipLine.hidden = !blip || !blip.busy;
    if (blip && blip.busy) {
      const waiting = blip.queue.map((person) => person.name);
      blipLine.textContent = `Blip is answering ${blip.busy.name}.` + (waiting.length ? ` Waiting: ${waiting.join(", ")}.` : "");
    }
  }

  const messageRow = (message) => {
    const row = element("li", message.kind && message.kind !== "say" ? `buddies-${message.kind}` : "");
    const name = element("strong", "", message.kind === "blip-ask" ? `${message.name} asked Blip:` : message.name);
    name.style.color = message.kind === "blip-reply" ? "" : message.colour;
    row.append(name, " ", element("span", "", message.text));
    row.title = new Date(message.time).toLocaleTimeString();
    if (message.pictures && message.pictures.length) row.append(questionPictures(message));
    return row;
  };

  // A question's pictures under it: each shows large on a click, and hides
  // on this person's screen; the host can remove them for everyone.
  function questionPictures(message) {
    const row = element("div", "buddies-pictures");
    const shown = message.pictures.filter((picture) => !hiddenPictures.has(`${message.id}:${picture.hash}`));
    for (const picture of shown) {
      const image = element("img");
      image.alt = "A picture shown with the question";
      pictureUrl(picture).then((url) => { image.src = url; }, () => { image.alt = "The picture could not be fetched"; });
      const show = button("", "Show it large", () => {
        pictureUrl(picture).then((url) => {
          viewerPicture.src = url;
          viewer.hidden = false;
          viewer.focus();
        }, () => {});
      }, "buddies-thumb");
      show.append(image);
      const hide = button("Hide", "Hide this picture on your screen", () => {
        hiddenPictures.add(`${message.id}:${picture.hash}`);
        render();
      });
      const item = element("span", "buddies-picture");
      item.append(show, hide);
      row.append(item);
    }
    if (shown.length < message.pictures.length) row.append(element("span", "muted", "Hidden"));
    if (session && session.isHost) {
      row.append(button("Remove for everyone", "Take this question's pictures out of the session, for everyone",
        () => session.removePictures(message.id), "danger"));
    }
    return row;
  }

  function pictureUrl(picture) {
    if (!pictureUrls.has(picture.hash)) {
      const current = session;
      const promise = current.download(picture.hash).then((blob) => URL.createObjectURL(new Blob([blob], { type: picture.type })));
      promise.catch(() => pictureUrls.delete(picture.hash));
      pictureUrls.set(picture.hash, promise);
    }
    return pictureUrls.get(picture.hash);
  }

  function forgetPictures() {
    for (const promise of pictureUrls.values()) promise.then((url) => URL.revokeObjectURL(url), () => {});
    pictureUrls = new Map();
    hiddenPictures.clear();
    closeViewer();
  }

  inviteButton.addEventListener("click", () => {
    if (!session && !panel.hidden) return toggle(false);
    if (!session && signedIn()) return start();
    toggle();
  });

  // The others' cursors in the file shown, and the files they have open.
  function showPresence() {
    if (cursorFrame) return;
    cursorFrame = requestAnimationFrame(() => {
      cursorFrame = 0;
      if (!session || !session.project) return;
      if (editor.path) editor.showCursors(editor.path, session.cursorsIn(editor.path));
      ide.tree.showPresence(session.openFiles());
      if (following) follow();
    });
  }
  // The names show beside the cursors only while their people type.
  setInterval(() => { if (session && session.project) showPresence(); }, 1000);

  // Follow: the editor goes to the person's file, and keeps their cursor in
  // view, until this person clicks or types.
  function setFollowing(id) {
    following = id;
    render();
    if (id) follow();
  }
  let followedFile = null;
  async function follow() {
    const person = session && session.people.find((candidate) => candidate.id === following);
    if (!person) {
      following = null;
      return;
    }
    const state = session.presenceOf(person);
    if (!state || !state.file) return;
    if (state.file !== editor.path && session.project.has(state.file) && followedFile !== state.file) {
      followedFile = state.file;
      await ide.openFile(state.file);
      followedFile = null;
    }
    const cursor = session.cursorsIn(editor.path).find((candidate) => candidate.id === person.id);
    if (cursor) editor.reveal(cursor.head);
  }
  for (const type of ["mousedown", "keydown"]) {
    editor.view.dom.addEventListener(type, () => {
      if (following) setFollowing(null);
    });
  }

  // A session's events.
  function listen(current) {
    const on = (type, handler) => current.addEventListener(type, (event) => {
      if (session === current) handler(event.detail);
    });
    on("status", () => render());
    on("welcome", () => render());
    on("people", () => render());
    on("presence", () => showPresence());
    on("files", () => showPresence());
    on("meta", () => ide.metaChanged());
    on("links", () => render());
    on("options", () => render());
    on("key", (key) => keepKey(current.project, key));
    on("said", (message) => {
      // Others' messages, not this person's own nor Blip's answers to them,
      // show in the status bar while the panel is closed.
      const mine = current.you && message.from === current.you.id;
      if (panel.hidden && !mine) {
        ++unread;
        status(`${message.name}: ${message.text}`);
      }
      render();
    });
    on("error", (message) => notice(message, [], "buddies"));
    on("run", (message) => runArrived(message));
    on("blip-state", () => render());
    on("pictures-removed", () => render());
    on("ended", () => ended("The buddy session has ended."));
    on("removed", () => ended("You were removed from the buddy session."));
    // Refused when it reconnects, as when the session ended while this page
    // was away: the session is over here too, at once, so that nothing done
    // after it waits for a session that will not come back.
    on("refused", (message) => ended(message && message.message ? `${message.message}.` :
      "The buddy session could not be rejoined."));
  }

  // The host opens a session: the project goes to their account first.
  const start = attempt("The buddy session could not open", async () => {
    const project = ide.project();
    if (!project) return;
    if (!signedIn()) {
      toggle(true);
      return;
    }
    status("Opening a buddy session…");
    const cloudId = await ide.account.prepareSession(project);
    if (!cloudId) return;
    const { room } = await cloud.request("POST", `projects/${cloudId}/room`, { json: {} });
    await project.update({ buddies: { room: room.id, host: true } });
    await connect(project, { room: room.id, token: "" });
    toggle(true);
    status("Your buddy session is open. Copy a link and send it to the people you are inviting.");
  });

  // Joins a session from a link: #join=<room>.<token>. as: "watch" to watch
  // with an edit link while signed out.
  const join = attempt("The buddy session could not be joined", async (room, token, as) => {
    clearNotices("buddies");
    // A project from this session in this browser already: from a reload,
    // or from joining before.
    const existing = (await store.list()).find((meta) => meta.buddies && meta.buddies.room === room && !meta.buddies.ended);
    const current = new BuddySession({ cloud, room, token, as, key: (existing && existing.buddies.key) || "",
      isText: ide.isText });
    try {
      await current.ready;
    } catch (error) {
      current.close();
      refused(error, room, token);
      return false;
    }
    let project;
    if (existing) {
      project = await store.openProject(existing.id);
      await project.update({ buddies: { ...existing.buddies, token, as: as || null } });
    } else {
      const name = `${current.name} (with ${current.room.host})`;
      project = await store.create({ name, files: [], main: current.meta.get("main") || null,
        mode: current.meta.get("mode") || "extended" });
      await project.update({ buddies: { room, token, as: as || null, host: false, hostName: current.room.host } });
    }
    await use(current, project);
    await ide.show(project);
    status(current.canEdit ? `You're in ${current.room.host}'s buddy session. Your changes reach everyone as you make them.` :
      `You're watching ${current.room.host}'s buddy session.`);
    return true;
  });

  function refused(error, room, token) {
    if (error.reason === "sign-in" && cloud.canUsePasskeys) {
      notice(`${error.message}.`, [
        ["Sign in", () => {
          pendingJoin = { room, token };
          $("account-button").click();
        }],
        ...(error.message.includes("watch") ? [["Watch", () => join(room, token, "watch")]] : []),
      ], "buddies");
    } else {
      notice(`${error.message}.`, [], "buddies");
    }
  }
  let pendingJoin = null;

  // Connects to a session for a project here.
  async function connect(project, { room, token, as, key }) {
    const current = new BuddySession({ cloud, room, token, as, key, isText: ide.isText });
    try {
      await current.ready;
    } catch (error) {
      current.close();
      throw error;
    }
    await use(current, project);
  }

  async function use(current, project) {
    if (session) session.close();
    session = current;
    following = null;
    runsAllowed = false;
    listen(current);
    // A guest keeps the key the room gave their page, to come back with after
    // a reload, even once the host has made new links.
    await keepKey(project, current.key);
    await current.attach(project, editor, {
      refreshFiles: () => {
        if (ide.project() === project) ide.refreshFiles();
        showPresence();
      },
      closeFile: (path) => ide.closeFile(path),
      metaChanged: () => ide.metaChanged(),
    });
    clearNotices("buddies");
    if (!current.canEdit) {
      notice(`You're watching ${current.room.host}'s buddy session. Your changes here are not shared.`,
        [["Save a copy", () => saveCopy()]], "buddies");
    }
    render();
    changed();
  }

  // A session began or ended here.
  function changed() {
    if (ide.sessionChanged) ide.sessionChanged();
  }

  async function keepKey(project, key) {
    const info = project && project.meta.buddies;
    if (!info || info.host || !key || info.key === key) return;
    await project.update({ buddies: { ...info, key } });
  }

  // The session's project shown or not: the session follows the project.
  async function projectChanged(project) {
    if (session && session.project !== project) {
      session.close();
      session = null;
      following = null;
      ide.tree.showPresence(new Map());
      changed();
    }
    render();
    if (!session) {
      editor.setReadOnly(false);
      if (!project || !project.meta.buddies) {
        clearNotices("buddies");
        clearNotices("buddies-run");
      }
    }
    // A guest's copy of a session that has ended stays read-only, to copy.
    if (project && !session && project.meta.buddies && project.meta.buddies.ended && !project.meta.buddies.host) {
      editor.setReadOnly(true);
      clearNotices("buddies");
      notice("This buddy session has ended. You can keep a copy of the program.",
        [["Save a copy", () => saveCopy(project)]], "buddies");
      return;
    }
    if (!project || session || !project.meta.buddies || project.meta.buddies.ended) return;
    const info = project.meta.buddies;
    if (info.host) {
      // The host's session, if it is still open.
      if (!signedIn() || !project.meta.cloud) return;
      try {
        const { room } = await cloud.request("GET", `projects/${project.meta.cloud.id}/room`);
        if (!room || room.id !== info.room) {
          await project.update({ buddies: null });
          await ide.account.resumeAfterSession(project);
          render();
          return;
        }
        await connect(project, { room: info.room, token: "" });
      } catch (error) {
        notice(`Your buddy session could not be reopened: ${error.message}`, [], "buddies");
      }
    } else {
      try {
        await connect(project, { room: info.room, token: info.token, as: info.as || undefined, key: info.key || "" });
      } catch (error) {
        if (error.reason === "ended" || error.reason === "removed" || error.reason === "link") {
          await project.update({ buddies: { ...info, ended: true } });
          ended(`${error.message}.`, project);
        } else {
          notice(`The buddy session could not be joined: ${error.message}`, [], "buddies");
        }
      }
    }
  }

  // The session is over for this person: the host's project goes back to
  // saving to their account; a guest's stays, read-only, to copy.
  // The room says so as the host ends it, so this may be called twice at once:
  // the second waits for the first.
  let ending = null;
  function ended(message, project = session && session.project) {
    ending = ending || finish(message, project).finally(() => { ending = null; });
    return ending;
  }
  async function finish(message, project) {
    const current = session;
    if (current) {
      await current.settle().catch(() => {});
      current.close();
      if (session === current) session = null;
      changed();
    }
    following = null;
    forgetPictures();
    ide.tree.showPresence(new Map());
    if (!project) return render();
    if (project.meta.buddies && project.meta.buddies.host) {
      await project.update({ buddies: null });
      await ide.account.resumeAfterSession(project);
      status("The buddy session has ended. Your project has every change, and saves to your account again.");
    } else {
      await project.update({ buddies: { ...(project.meta.buddies || {}), ended: true } });
      if (ide.project() === project) editor.setReadOnly(true);
      clearNotices("buddies");
      notice(`${message} You can keep a copy of the program.`, [["Save a copy", () => saveCopy(project)]], "buddies");
    }
    render();
  }

  const endSession = attempt("The buddy session could not be ended", async () => {
    if (!session || !session.isHost) return;
    if (!(await ide.ask({ title: "End the buddy session?", message: "Everyone leaves the session. Your project " +
      "keeps every change made in it.", action: "End session" }))) return;
    const project = session.project;
    await cloud.request("DELETE", `rooms/${session.roomId}`);
    await ended("The buddy session has ended.", project);
  });

  // Removing someone also makes new links, so that they cannot come back with
  // the old ones, signed in or not; the others stay.
  const removePerson = attempt("That person could not be removed", async (person) => {
    if (!(await ide.ask({ title: `Remove ${person.name}?`, message: `${person.name} leaves the session and cannot ` +
      "come back. The invite links are made new, so the old ones stop working: send the new ones to anyone who has " +
      "still to join. Everyone else here stays.", action: "Remove" }))) return;
    const { room } = await cloud.request("DELETE", `rooms/${session.roomId}/people/${person.id}`);
    if (room && session && session.room) session.room.links = { edit: room.editToken, watch: room.watchToken };
    render();
    status(`Removed ${person.name}. The invite links are new, and the old ones no longer work.`);
  });

  const leave = attempt("The session could not be left", async () => {
    if (!session || session.isHost) return;
    const project = session.project;
    session.close();
    session = null;
    changed();
    await project.update({ buddies: { ...project.meta.buddies, ended: true } });
    editor.setReadOnly(true);
    clearNotices("buddies");
    notice("You left the buddy session. You can keep a copy of the program.",
      [["Save a copy", () => saveCopy(project)]], "buddies");
    toggle(false);
    render();
  });

  // A copy of the session's program, the person's own.
  const saveCopy = attempt("The copy could not be made", async (project = session ? session.project : ide.project()) => {
    if (!project) return;
    if (session) await session.settle();
    await ide.saveAll();
    const name = project.name.replace(/ \(with [^)]*\)$/, "");
    const files = project.paths().map((path) => [path, project.blob(path)]);
    const copy = await store.create({ name, files, main: project.main, mode: project.meta.mode });
    await ide.show(copy);
    status(`Saved a copy of the program as ${copy.name}. It is yours to change.`);
  });

  // Run for everyone: the host's files as they are now, by hash, sent
  // through the room; each page that allows it runs exactly those files.
  const runForEveryone = attempt("The program could not be run for everyone", async () => {
    if (!session || !session.isHost) return;
    await ide.saveAll();
    await session.settle();
    const project = session.project;
    const manifest = {};
    for (const path of project.paths()) {
      const shared = session.textOf(path);
      const blob = shared !== null ? new Blob([shared]) : project.blob(path);
      const hash = await hashBlob(blob);
      await session.upload(hash, blob);
      manifest[path] = hash;
    }
    session.sendJson({ type: "run", manifest, main: project.main, mode: project.meta.mode || "extended" });
    status("Running the program for everyone.");
    await ide.runFiles({ files: project.paths().map((path) => {
      const shared = session.textOf(path);
      return [path, shared !== null ? new Blob([shared]) : project.blob(path)];
    }), main: project.main, mode: project.meta.mode || "extended" });
  });

  async function runArrived(message) {
    if (session.isHost) return;
    if (!runsAllowed) {
      clearNotices("buddies-run");
      notice(`${message.by} wants to run the program on your screen.`, [
        ["Allow for this session", () => {
          runsAllowed = true;
          clearNotices("buddies-run");
          runManifest(message);
        }],
        ["Not now", () => clearNotices("buddies-run")],
      ], "buddies-run");
      return;
    }
    await runManifest(message);
  }

  const runManifest = attempt("The program could not be run", async (message) => {
    const current = session;
    if (!current) return;
    const files = [];
    for (const [path, hash] of Object.entries(message.manifest)) {
      const shared = current.textOf(path);
      if (shared !== null && await hashBlob(new Blob([shared])) === hash) {
        files.push([path, new Blob([shared])]);
      } else if (current.project.has(path) && await hashBlob(current.project.blob(path)) === hash) {
        files.push([path, current.project.blob(path)]);
      } else {
        files.push([path, await current.download(hash)]);
      }
    }
    status(`${message.by} is running the program for everyone.`);
    await ide.runFiles({ files, main: message.main, mode: message.mode, by: message.by });
  });

  // Catch me up: Blip says what the others changed since this person joined,
  // or last asked, from the room's note of who changed which files and from
  // how their text changed.
  const catchUp = attempt("Blip could not catch you up", async () => {
    if (!session || !ide.blip()) return;
    const since = session.lookedAt || 0;
    const time = (when) => new Date(when).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    const activity = (await session.activitySince(since)).filter((entry) => !session.you || entry.by !== session.you.id);
    const who = activity.map((entry) => `${time(entry.time)} ${entry.name} changed ${entry.files.join(", ")}`);
    const changes = [];
    const before = session.snapshot || new Map();
    for (const [path, old] of before) {
      const now = session.textOf(path);
      if (now === null) changes.push(`${path} was removed.`);
      else if (now !== old) changes.push(textChange(path, old, now));
    }
    for (const path of session.files.keys()) if (!before.has(path)) changes.push(`${path} was added.`);
    session.takeSnapshot();
    if (!who.length && !changes.length) {
      status("Nothing has changed since you last looked.");
      return;
    }
    let text = `Catch me up: in a few short points, say what the others in this buddy session changed since ${time(since)}, ` +
      `and what it means for the program.\n\nWho changed what:\n${who.join("\n") || "(no note)"}\n\nThe changes:\n` +
      changes.join("\n\n");
    if (text.length > 24000) text = `${text.slice(0, 24000)}\n... (more changes, left out)`;
    toggle(false);
    ide.blip().ask(text, { label: "Catch me up" });
  });

  // A file's change: the rows that differ, before and after, shortened.
  function textChange(path, before, after) {
    const a = before.split("\n"), b = after.split("\n");
    let start = 0;
    while (start < a.length && start < b.length && a[start] === b[start]) ++start;
    let end = 0;
    while (end < a.length - start && end < b.length - start && a[a.length - 1 - end] === b[b.length - 1 - end]) ++end;
    const clip = (rows) => rows.length > 40 ? [...rows.slice(0, 40), `... and ${rows.length - 40} more rows`] : rows;
    const removed = clip(a.slice(start, a.length - end)).map((row) => `- ${row}`);
    const added = clip(b.slice(start, b.length - end)).map((row) => `+ ${row}`);
    return `${path}, from row ${start + 1}:\n${[...removed, ...added].join("\n")}`;
  }

  // Blip's turn in the session, which his panel (blip/panel.js) waits for
  // before each request: null outside a session. Resolves when it is this
  // person's turn; meanwhile waiting(name) says whom he is answering. While
  // he works, his edits show as theirs, labelled with his name. done(reply)
  // ends the turn, with his answer for everyone's log.
  async function blipTurn(text, signal, waiting, pictures = []) {
    const current = session;
    if (!current || current.stopped || !current.canEdit || !current.you || !current.you.signedIn) return null;
    // The pictures shown with the question go in the room's files first, for
    // everyone's log; one that could not is left out of it.
    const shown = [];
    for (const picture of pictures) {
      try {
        const blob = new Blob([picture.bytes], { type: picture.type });
        const hash = await hashBlob(blob);
        await current.upload(hash, blob);
        shown.push({ hash, type: picture.type, width: picture.width, height: picture.height });
      } catch (error) {
        console.warn("A picture could not be shown to the session:", error);
      }
    }
    const busy = current.blipState && current.blipState.busy;
    if (busy && busy.id !== current.you.id) waiting(busy.name);
    const stop = () => current.blipDone("");
    signal.addEventListener("abort", stop, { once: true });
    try {
      await current.blipTurn(text, shown);
    } finally {
      signal.removeEventListener("abort", stop);
    }
    current.setPresence({ blip: true });
    return {
      done(reply) {
        current.setPresence({ blip: false });
        current.blipDone(reply);
      },
    };
  }

  // Ask Blip to combine: when the room refuses one of this person's world
  // edits because someone changed the same things first, Blip can make one
  // change that keeps theirs and does what this person meant too.
  // details: { mine, winner }, each { name, label, operations }.
  function offerCombine({ mine, winner }) {
    if (!session || !session.canEdit) return;
    const what = winner.label ? ` (${winner.label})` : "";
    const buttons = ide.blip() && session.you && session.you.signedIn ? [["Ask Blip to combine", () => {
      clearNotices("buddies-combine");
      let text = `Combine two changes to the World Editor's world. Mine${mine.label ? ` (${mine.label})` : ""} was ` +
        `taken back, because ${winner.name} changed the same things first${what}. Make one change that keeps ` +
        `${winner.name}'s and does what mine meant as well, where they agree; where they disagree, keep ` +
        `${winner.name}'s and tell me what you left out.\n\nMy change, as world edit operations:\n` +
        `${JSON.stringify(mine.operations)}\n\n${winner.name}'s change:\n` +
        `${winner.operations ? JSON.stringify(winner.operations) : "(not seen here: read the world as it is now)"}`;
      if (text.length > 24000) text = `${text.slice(0, 24000)}\n... (cut short)`;
      toggle(false);
      ide.blip().ask(text, { label: `Combine my change with ${winner.name}'s` });
    }]] : [];
    clearNotices("buddies-combine");
    notice(`${winner.name} changed it first${what}, so your change was taken back.`, buttons, "buddies-combine");
  }

  // Signing in finishes a join that asked for it.
  function accountChanged() {
    render();
    if (pendingJoin && signedIn()) {
      const { room, token } = pendingJoin;
      pendingJoin = null;
      join(room, token);
    }
  }

  // An invite link in the page's address.
  async function openInvite() {
    const invited = parseInvite(location.hash);
    if (!invited) return false;
    history.replaceState(null, "", location.pathname + location.search);
    return join(invited.room, invited.token);
  }

  render();
  return {
    toggle, render, projectChanged, accountChanged, openInvite, join, blipTurn, offerCombine,
    // The file shown changed.
    fileShown(path) {
      if (session && session.project) {
        session.fileShown(path);
        showPresence();
      }
    },
    // A shared file's text, which may be newer than the project's.
    textOf(path) {
      return session && session.project === ide.project() ? session.textOf(path) : null;
    },
    get session() { return session; },
  };
}
